import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import Sidebar from "../../../DesignSystem/Sidebar";
import AnnouncementDetailsForm from "./AnnouncementDetailsForm";
import AnnouncementStatsTab from "../components/AnnouncementStatsTab";
import Tabs from "../../../DesignSystem/Tabs/Tabs";


import {
  createAnnouncement,
  updateAnnouncement,
  updateAnnouncementStatus,
  subscribeAnnouncementById,
} from "../utils/announcementService";

import {
  canEditAnnouncement,
  getComputedAnnouncementStatus,
} from "../utils/announcementStatusUtils";

import "../../../../css/Marketing/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import "../../../../css/Marketing/Announcements/NewAnnouncementPage.css";
import { useServerSyncedTime } from "../../../../utils/useServerSyncedTime";

const getStatusLabel = (status) => {
  if (status === "draft") return "Draft";
  if (status === "scheduled") return "Scheduled";
  if (status === "archived") return "Archived";
  return "Active";
};

const getAnnouncementDetailsTitle = (status) => {
  if (status === "draft") return "Draft Announcement Details";
  if (status === "scheduled") return "Scheduled Announcement Details";
  if (status === "active") return "Active Announcement Details";
  if (status === "archived") return "Archived Announcement Details";

  return "Announcement Details";
};

const getReadOnlyMessage = (status) => {
  if (status === "archived") {
    return "This announcement has been archived. Unarchive it from the announcement options to edit it again.";
  }

  return "This announcement can only be viewed.";
};

const NewAnnouncementPage = () => {
  const navigate = useNavigate();
  const { announcementId } = useParams();

  const isExistingAnnouncement = Boolean(announcementId);

  const { effectiveTimestamp } = useServerSyncedTime();

  const [activeTab, setActiveTab] = useState("details");
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(isExistingAnnouncement);
  const [saving, setSaving] = useState(false);

  const announcementTabs = [
    { id: "details", label: "Post Management" },
    { id: "statistics", label: "Statistics" },
  ];

  useEffect(() => {
    if (!announcementId) {
      setAnnouncement(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeAnnouncementById(
      announcementId,
      (data) => {
        if (!data) {
          navigate("/department/marketing/announcements");
          return;
        }

        setAnnouncement(data);
        setLoading(false);
      },
      (error) => {
        // console.error("Failed to subscribe to announcement:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe?.();
  }, [announcementId, navigate]);

  const computedStatus = useMemo(() => {
    if (!announcement) return "";
    return getComputedAnnouncementStatus(announcement, effectiveTimestamp);
  }, [announcement, effectiveTimestamp]);

  const statusLabel = getStatusLabel(computedStatus);

  const editable = useMemo(() => {
    if (!isExistingAnnouncement) return true;
    if (!announcement) return false;

    return canEditAnnouncement(announcement, effectiveTimestamp);
  }, [announcement, isExistingAnnouncement, effectiveTimestamp]);

  const formMode = !isExistingAnnouncement
    ? "create"
    : editable
      ? "edit"
      : "review";

  const pageTitle = isExistingAnnouncement
    ? getAnnouncementDetailsTitle(computedStatus)
    : "New Announcement";

  const handleSaveDraft = async (formData) => {
    try {
      setSaving(true);

      if (isExistingAnnouncement) {
        await updateAnnouncement(announcementId, {
          ...formData,
          status: "draft",
        });
      } else {
        await createAnnouncement({
          formData,
          status: "draft",
          createdBy: localStorage.getItem("userEmail") || "",
        });
      }

      navigate("/department/marketing/announcements");
    } catch (error) {
      // console.error("Failed to save announcement draft:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setSaving(true);

      if (isExistingAnnouncement) {
        if (!editable) {
          await updateAnnouncementStatus(announcementId, "archived");
        } else {
          await updateAnnouncement(announcementId, {
            ...formData,
            status: formData.scheduleAnnouncement ? "scheduled" : "active",
          });
        }
      } else {
        await createAnnouncement({
          formData,
          status: formData.scheduleAnnouncement ? "scheduled" : "active",
          createdBy: localStorage.getItem("userEmail") || "",
        });
      }

      navigate("/department/marketing/announcements");
    } catch (error) {
      // console.error("Failed to save announcement:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Create and manage company announcements."
        />
      </Helmet>

      <Sidebar />

      <main className="Main-Container AnnouncementsPage">
        <Breadcrumb
          items={[
            { label: "Marketing", to: "/Department/Marketing" },
            {
              label: "Announcements",
              to: "/department/marketing/announcements",
            },
            { label: pageTitle },
          ]}
        />

        <div className="fp-admin-head">
          <h2 className="FP_H3 announcement-page-title-with-status">
            {pageTitle}
          </h2>

          {isExistingAnnouncement && (
            <span
              className={`FP_Content_Semibold announcement-status announcement-status--${statusLabel.toLowerCase()}`}
            >
              {statusLabel}
            </span>
          )}
        </div>

        {loading ? (
          <div className="announcements-empty FP_Content">
            Loading announcement...
          </div>
        ) : (
          <>
            {isExistingAnnouncement && (
              <div className="announcement-details-tabs-wrap">
                <Tabs
                  tabs={announcementTabs}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
              </div>
            )}

            {activeTab === "details" && (
              <AnnouncementDetailsForm
                mode={formMode}
                loading={saving}
                initialValues={announcement || undefined}
                onSaveDraft={handleSaveDraft}
                onSubmit={handleSubmit}
                readOnlyMessage={getReadOnlyMessage(computedStatus)}
              />
            )}

            {isExistingAnnouncement && activeTab === "statistics" && (
              <AnnouncementStatsTab announcement={announcement} />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default NewAnnouncementPage;
